import type { ReactiveMap } from '@solid-primitives/map'
import { DEV } from 'solid-js'
import { Color, InstancedBufferAttribute, InstancedMesh, MeshLambertMaterial, SphereGeometry, type WebGLProgramParametersWithUniforms } from 'three'
import type { NeighborElevationCollection } from '../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap'
import type { Nullable } from '../../../../../typescript'
import { EntityTypeId } from '../../../../core/entity/EntityTypeId.enum'
import type { Entity } from '../../../../core/entity/index'
import { createNewRNGConfig, nextNumberRange } from '../../../../core/rng/index'
import resetEntityRngConfig from '../../../../core/rng/resetEntityRngConfig'
import tmpRngWith from '../../../../core/rng/tmpRngWith'
import { VERTICES_PER_TILE } from '../../../consts'
import createCamoMapTexture from '../../../fn/createCamoMapTexture'
import setRandomQuaternion from '../../../fn/setRandomQuaternion'
import { dummyMatrix, dummyObject3D, dummyPosition, dummyQuaternion, resetDummyObject3D } from '../../DummyObject3D/dummyObject3D'
import getTerrainMesh1TileElevationV2 from '../../TerrainMesh1V1/getTerrainMesh1TileElevationV2'
import getTerrainMesh1Z from '../../TerrainMesh1V2/getTerrainMesh1Z'
import { boulderFragmentShader, boulderVertexShader } from './boulderShaders'
import { getBoulderColorHexNumber } from './colors'

const lowResBoulderNoise = createCamoMapTexture(tmpRngWith(1), 16, 8)

const tileRadius = VERTICES_PER_TILE / 2

export default function createBoulderInstancedMeshV1Group(
  neighborBaseElevationReactiveMap: ReactiveMap<number, NeighborElevationCollection>,
  ents: Array<Entity>,
  stateWidth: number,
) {
  // console.log('createBoulderInstancedMeshV1Group', ents.map(ent => ent.id))
  resetDummyObject3D()

  const count = ents.length

  const radius = 0.5
  const widthSegments = 5
  const heightSegments = 4
  const geometry = new SphereGeometry(radius, widthSegments, heightSegments)
  const boulderMaterial = new MeshLambertMaterial({
    map: lowResBoulderNoise,
  })
  boulderMaterial.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.vertexShader = boulderVertexShader
    shader.fragmentShader = boulderFragmentShader
  }
  const mesh = new InstancedMesh(geometry, boulderMaterial, count)

  const rng = createNewRNGConfig()

  const colors = new Float32Array(count * 3)
  const baseElevations = new Float32Array(count)
  const boulderHeights = new Float32Array(count)
  const boulderBlendStarts = new Float32Array(count)
  const boulderBlendEnds = new Float32Array(count)
  const boulderTintColors = new Float32Array(count * 3) // RGB for each boulder
  const color = new Color
  let boulderTopColor: Color
  for (let i = 0; i < count; i++) {
    const ent = ents[i]
    const { id, etype_id, x, y } = ent
    const nodeIndex = x + (y * stateWidth)
    resetEntityRngConfig(id, rng)
    color.setHex(getBoulderColorHexNumber(etype_id, rng))
    const elevationJitter = nextNumberRange(-0.1, 0.2, rng)

    // (-5, 5) * 0.9
    const tileX = (nextNumberRange(-tileRadius, tileRadius, rng) * 0.9)
    const tileY = (nextNumberRange(-tileRadius, tileRadius, rng) * 0.9)

    const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
    const elevation = baseElevationsCollection
      ? getTerrainMesh1Z(baseElevationsCollection, tileX, tileY)
      : getTerrainMesh1TileElevationV2(ent)

    if (DEV) {
      if (!baseElevationsCollection) {
        console.log('!baseElevationsCollection')
      }
    }

    const wx = x * VERTICES_PER_TILE + tileX
    const wy = y * VERTICES_PER_TILE + tileY
    const rh = elevation + elevationJitter
    dummyPosition.set(wx, rh, wy)

    setRandomQuaternion(rng, dummyQuaternion)

    dummyObject3D.updateMatrix()

    mesh.setMatrixAt(i, dummyMatrix)

    colors[(i * 3)] = color.r
    colors[(i * 3) + 1] = color.g
    colors[(i * 3) + 2] = color.b
    
    baseElevations[i] = rh
    boulderHeights[i] = 2 * radius
        
    boulderTopColor = getBoulderTopColor(etype_id) || color
    // Set blend range (adjust these as needed)
    boulderBlendStarts[i] = 0.8
    boulderBlendEnds[i] = 1
    if (boulderTopColor === color) {
      boulderBlendStarts[i] = 0
      boulderBlendEnds[i] = 0
    }
    
    // Set the tint color in the array (RGB)
    boulderTintColors[i * 3] = boulderTopColor.r
    boulderTintColors[i * 3 + 1] = boulderTopColor.g
    boulderTintColors[i * 3 + 2] = boulderTopColor.b
  }
  mesh.instanceColor = new InstancedBufferAttribute(colors, 3)
  mesh.geometry.setAttribute('baseElevation', new InstancedBufferAttribute(baseElevations, 1))
  mesh.geometry.setAttribute('boulderHeight', new InstancedBufferAttribute(boulderHeights, 1))
  mesh.geometry.setAttribute('boulderBlendStart', new InstancedBufferAttribute(boulderBlendStarts, 1))
  mesh.geometry.setAttribute('boulderBlendEnd', new InstancedBufferAttribute(boulderBlendEnds, 1))
  mesh.geometry.setAttribute('boulderTintColor', new InstancedBufferAttribute(boulderTintColors, 3))

  mesh.frustumCulled = true
  mesh.castShadow = false
  mesh.receiveShadow = true

  // mesh.visible = false

  return mesh
}

const boulderTopSnowColor = new Color(0.9, 0.9, 1.0) // Snow white with blue tint
const boulderTopForestColor = new Color(0.6, 0.8, 0.4) // Light green moss
const boulderTopJungleColor = new Color(0.2, 0.6, 0.2) // Dense green moss
const boulderTopDeadwoodColor = new Color(0.5, 0.5, 0.5) // Ashy dust or fungal bloom

function getBoulderTopColor(etype_id: EntityTypeId): Nullable<Color> {
  // Use type guards to determine the entity type
  if (etype_id === EntityTypeId.Snow) {
    return boulderTopSnowColor
  } else if (etype_id === EntityTypeId.Forest) {
    return boulderTopForestColor
  } else if (etype_id === EntityTypeId.Jungle) {
    return boulderTopJungleColor
  } else if (etype_id === EntityTypeId.Deadwood) {
    return boulderTopDeadwoodColor
  }
}
